/**
 * Central de notificações do fluxo editorial: tipos e funções puras usadas
 * pelo sino, pelo painel da Toolbar e pela página `/notificacoes`.
 */

export type FiltroNotificacoes = 'todas' | 'nao_lidas' | 'acao_necessaria';

export type CategoriaNotificacao =
  | 'acao_necessaria'
  | 'informativa'
  /** Registro da própria ação de quem a fez (nasce lido). */
  | 'acompanhamento';

export type FluxoNotificacao = 'noticia' | 'pagina';

export type Notificacao = {
  '@id': string;
  id: number;
  tipo: string;
  categoria: CategoriaNotificacao;
  fluxo?: FluxoNotificacao;
  portal_type?: string;
  uid: string;
  titulo: string;
  url: string;
  path: string;
  disponivel: boolean;
  transicao: string;
  transicao_titulo: string;
  de_estado: string | null;
  de_estado_titulo: string | null;
  para_estado: string | null;
  para_estado_titulo: string | null;
  ator: { id: string; nome: string };
  comentario: string;
  /** O que quem validou mudou no texto antes de decidir ("o título", "o texto"). */
  alteracoes?: string[];
  created: string;
  read_at: string | null;
  lida: boolean;
};

export type ContagemNotificacoes = {
  nao_lidas: number;
  acao_necessaria: number;
};

export type ItemFila = {
  '@id': string;
  uid: string;
  title: string;
  portal_type?: string;
  fluxo?: FluxoNotificacao;
  review_state: string;
  review_state_title: string;
  acao: string;
  /** Sugestão, não pendência: a publicação nacional é opcional. */
  opcional?: boolean;
  unidadeOrigem: string | null;
  modified: string | null;
};

/** Badge do sino: nada abaixo de 1, "99+" acima de 99. */
export const formatarContagem = (n: number | null | undefined): string => {
  if (!n || n < 1) return '';
  return n > 99 ? '99+' : String(n);
};

/** Rótulo acessível do botão do sino. */
export const rotuloSino = (n: number | null | undefined): string => {
  if (!n || n < 1) return 'Notificações';
  return n === 1
    ? 'Notificações, 1 não lida'
    : `Notificações, ${n > 99 ? 'mais de 99' : n} não lidas`;
};

/** O que aconteceu, em uma frase: "Maria enviou para revisão de texto". */
const VERBOS: Record<string, string> = {
  // Workflow padrão do Plone (páginas) e atalhos administrativos da notícia.
  submit: 'enviou para revisão',
  publish: 'publicou',
  reject: 'devolveu para correção',
  retract: 'retirou da publicação',
  show: 'publicou localmente',
  enviar_revisao: 'enviou para revisão de texto',
  cancelar_revisao: 'cancelou a revisão de texto',
  concluir_revisao: 'concluiu a revisão de texto',
  devolver_revisao: 'devolveu para revisão de texto',
  publicar_local: 'publicou localmente',
  publicar_local_direto: 'publicou localmente',
  publicar_nacional: 'publicou nacionalmente',
  publicar_nacional_direto: 'publicou nacionalmente',
  enviar_revisao_nacional: 'enviou para publicação nacional',
  encaminhar_tecnica: 'encaminhou para revisão técnica',
  efetivar: 'efetivou a publicação nacional',
  revogar: 'revogou a publicação nacional',
  iniciar_revisao_publicada: 'iniciou a revisão do conteúdo publicado',
  concluir_revisao_publicada: 'concluiu a revisão do conteúdo publicado',
  cancelar_revisao_publicada: 'cancelou a revisão do conteúdo publicado',
  aceitar_alteracoes: 'aceitou as alterações da revisão',
  rejeitar_alteracoes: 'rejeitou as alterações da revisão',
  hide: 'tornou privada',
  // Não é transição: alguém atualizou a notícia já Publicada Nacional (o
  // Editor nacional, os Revisores nacionais e o autor são avisados só para
  // conhecimento).
  atualizar_publicada: 'atualizou a notícia publicada nacionalmente',
};

/** "efetivou a publicação nacional"; transição desconhecida vira "alterou". */
export const verboDaTransicao = (transicao: string, titulo?: string): string =>
  VERBOS[transicao] || (titulo ? `fez "${titulo}"` : 'alterou');

export const descreverNotificacao = (
  n: Notificacao,
  usuarioAtual?: string | null,
): string => {
  const ator =
    usuarioAtual && n.ator?.id === usuarioAtual
      ? 'Você'
      : n.ator?.nome || n.ator?.id || 'Alguém';
  const verbo = verboDaTransicao(n.transicao, n.transicao_titulo);
  const alteracoes = n.alteracoes?.length
    ? ` e alterou ${listarEmPortugues(n.alteracoes)}`
    : '';
  return `${ator} ${verbo}${alteracoes}`;
};

/** "a", "a e b", "a, b e c". */
const listarEmPortugues = (itens: string[]): string =>
  itens.length > 1
    ? `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`
    : itens[0];

/** Ícone (nome lógico) por tipo de transição — o componente mapeia para SVG. */
export type TomNotificacao = 'acao' | 'sucesso' | 'alerta' | 'info';

const TRANSICOES_DE_SUCESSO = [
  'efetivar',
  'publicar_local',
  'publicar_local_direto',
  'publicar_nacional',
  'publicar_nacional_direto',
  'aceitar_alteracoes',
  'publish',
  'show',
];

export const tomDaNotificacao = (n: Notificacao): TomNotificacao => {
  if (
    ['revogar', 'devolver_revisao', 'reject', 'rejeitar_alteracoes'].includes(
      n.transicao,
    )
  )
    return 'alerta';
  if (TRANSICOES_DE_SUCESSO.includes(n.transicao)) return 'sucesso';
  return n.categoria === 'acao_necessaria' ? 'acao' : 'info';
};

const UNIDADES: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];

/**
 * "há 5 minutos", "ontem"... Recebe `agora` explícito: chamar `Date.now()` no
 * render quebraria a hidratação — o componente só calcula isso no cliente.
 */
export const tempoRelativo = (iso: string, agora: number): string => {
  const momento = new Date(iso).getTime();
  if (Number.isNaN(momento)) return '';
  const segundos = Math.round((momento - agora) / 1000);
  if (Math.abs(segundos) < 60) return 'agora';
  const rtf = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });
  for (const [unidade, tamanho] of UNIDADES) {
    if (Math.abs(segundos) >= tamanho) {
      return rtf.format(Math.round(segundos / tamanho), unidade);
    }
  }
  return 'agora';
};

/** Data completa para o `title`/`dateTime` do tempo relativo. */
export const dataCompleta = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
};

/** Intervalo do polling, com backoff exponencial em erro (teto de 5 min). */
export const POLLING_MS = 60_000;
export const POLLING_MAX_MS = 5 * 60_000;

export const proximoIntervalo = (falhasSeguidas: number): number =>
  Math.min(POLLING_MS * 2 ** Math.max(falhasSeguidas, 0), POLLING_MAX_MS);

/**
 * Transições do fluxo editorial que pedem comentário antes de seguir:
 * `obrigatorio` espelha `TRANSICOES_COM_COMENTARIO` no backend
 * (`fluxo_editorial.py`), que é quem de fato barra.
 */
export const COMENTARIO_DA_TRANSICAO: Record<
  string,
  { obrigatorio: boolean; titulo: string; dica: string }
> = {
  concluir_revisao: {
    obrigatorio: false,
    titulo: 'Concluir revisão de texto',
    dica: 'Apontamentos para o editor aplicar. Se não houver, deixe em branco.',
  },
  devolver_revisao: {
    obrigatorio: true,
    titulo: 'Devolver para revisão de texto',
    dica: 'Explique o que precisa ser revisto.',
  },
  revogar: {
    obrigatorio: true,
    titulo: 'Revogar publicação nacional',
    dica: 'Explique por que a publicação nacional foi revogada.',
  },
  concluir_revisao_publicada: {
    obrigatorio: false,
    titulo: 'Concluir revisão de conteúdo',
    dica: 'O que foi alterado, para o Editor nacional conferir (opcional).',
  },
  rejeitar_alteracoes: {
    obrigatorio: true,
    titulo: 'Rejeitar alterações',
    dica: 'Explique por que as alterações foram rejeitadas: a notícia volta ao texto anterior à revisão.',
  },
};

/** Id da transição a partir da URL do `@workflow` (`.../@workflow/<id>`). */
export const idDaTransicao = (url: string | undefined): string =>
  (url || '').split('/').filter(Boolean).pop() || '';

/* ------------------------------------------------------------------------
 * Etapas do fluxo (barra de progresso da notificação)
 * --------------------------------------------------------------------- */

export type SituacaoEtapa =
  | 'feita'
  | 'atual'
  | 'pendente'
  | 'pulada'
  | 'revogada';

export type Etapa = { id: string; rotulo: string; situacao: SituacaoEtapa };

/**
 * As etapas dos diagramas "Fluxo de Gestão de Publicação de Notícias" v2.6
 * (Editores locais e Editores Nacionais). A revisão de texto, a publicação
 * nacional e a revisão de conteúdo publicado são opcionais.
 */
const ETAPAS_NOTICIA: { id: string; rotulo: string; estados: string[] }[] = [
  { id: 'elaboracao', rotulo: 'Elaboração', estados: ['private'] },
  {
    id: 'revisao',
    rotulo: 'Revisão de texto',
    estados: ['em_revisao_regional'],
  },
  {
    id: 'validacao',
    rotulo: 'Validação da revisão',
    estados: ['aguardando_validacao'],
  },
  { id: 'local', rotulo: 'Publicado Local', estados: ['visible'] },
  {
    id: 'nacional',
    rotulo: 'Validação nacional',
    estados: ['revisao_textual_nacional', 'revisao_tecnica', 'pending'],
  },
  {
    id: 'publicado',
    rotulo: 'Publicado Nacional',
    estados: ['published', 'revogado'],
  },
  {
    id: 'revisao_publicada',
    rotulo: 'Revisão de conteúdo',
    estados: ['publicado_em_revisao'],
  },
  {
    id: 'validacao_alteracoes',
    rotulo: 'Validação das alterações',
    estados: ['publicado_aguardando_validacao'],
  },
];

/**
 * A barra cresce por fase: até "Publicado Local" só as etapas locais (a
 * publicação nacional é opcional), até "Publicado Nacional" as da nacional, e
 * as da revisão de conteúdo publicado (também opcional) só durante ela.
 */
const ULTIMA_ETAPA_LOCAL = ETAPAS_NOTICIA.findIndex((e) => e.id === 'local');
const ULTIMA_ETAPA_NACIONAL = ETAPAS_NOTICIA.findIndex(
  (e) => e.id === 'publicado',
);

/** Publicada sem passar pela revisão de texto (etapa opcional). */
const SEM_REVISAO_DE_TEXTO = ['private'];

/** Publicada direto na nacional pelo Editor nacional, sem o local. */
const DIRETO_NA_NACIONAL = ['private', 'aguardando_validacao'];

const ETAPAS_PAGINA: { id: string; rotulo: string; estados: string[] }[] = [
  { id: 'rascunho', rotulo: 'Rascunho', estados: ['private'] },
  { id: 'revisao', rotulo: 'Em revisão', estados: ['pending'] },
  { id: 'publicado', rotulo: 'Publicado', estados: ['published'] },
];

/**
 * Onde a notícia/página estava quando a notificação foi gerada.
 *
 * - as etapas opcionais que ficaram para trás aparecem como "puladas": a
 *   revisão de texto, quando a notícia foi publicada direto da elaboração, e
 *   o local e a validação nacional, quando o Editor nacional publicou direto;
 * - revogada, a última etapa aparece como "revogada" (a notícia segue no ar
 *   localmente);
 * - na fase local (até "Publicado Local") só as etapas locais aparecem, e
 *   "Publicado Local" vira o fim do fluxo.
 */
export const etapasDaNotificacao = (
  estado: string | null | undefined,
  fluxo: FluxoNotificacao = 'noticia',
  estadoAnterior?: string | null,
): Etapa[] => {
  const completa = fluxo === 'pagina' ? ETAPAS_PAGINA : ETAPAS_NOTICIA;
  const indiceAtual = completa.findIndex((e) =>
    e.estados.includes(estado || ''),
  );
  let fim = completa.length;
  if (fluxo !== 'pagina' && indiceAtual >= 0) {
    if (indiceAtual <= ULTIMA_ETAPA_LOCAL) fim = ULTIMA_ETAPA_LOCAL + 1;
    else if (indiceAtual <= ULTIMA_ETAPA_NACIONAL)
      fim = ULTIMA_ETAPA_NACIONAL + 1;
  }
  const definicao = completa.slice(0, fim);
  const atual = indiceAtual;
  return definicao.map((etapa, indice) => {
    let situacao: SituacaoEtapa = 'pendente';
    if (atual < 0) situacao = 'pendente';
    else if (indice < atual) situacao = 'feita';
    else if (indice === atual) situacao = 'atual';
    if (fluxo === 'noticia' && situacao === 'feita' && estadoAnterior) {
      const semRevisao =
        ['revisao', 'validacao'].includes(etapa.id) &&
        SEM_REVISAO_DE_TEXTO.includes(estadoAnterior);
      const diretoNaNacional =
        ['local', 'nacional'].includes(etapa.id) &&
        estado === 'published' &&
        DIRETO_NA_NACIONAL.includes(estadoAnterior);
      if (semRevisao || diretoNaNacional) situacao = 'pulada';
    }
    if (etapa.id === 'publicado' && indice === atual && estado === 'revogado') {
      return { ...etapa, rotulo: 'Nacional revogada', situacao: 'revogada' };
    }
    // Chegou ao fim: a última etapa está concluída, não "em andamento" —
    // salvo a validação das alterações, que ainda espera o Editor nacional.
    if (
      indice === atual &&
      indice === definicao.length - 1 &&
      estado !== 'publicado_aguardando_validacao'
    ) {
      situacao = 'feita';
    }
    return { id: etapa.id, rotulo: etapa.rotulo, situacao };
  });
};

/** "Etapa 2 de 7: Revisão de texto" — resumo acessível da barra. */
export const resumoDasEtapas = (etapas: Etapa[]): string => {
  const indice = etapas.findIndex(
    (e) => e.situacao === 'atual' || e.situacao === 'revogada',
  );
  if (indice < 0) {
    const ultima = etapas[etapas.length - 1];
    return ultima?.situacao === 'feita' ? `Concluído: ${ultima.rotulo}` : '';
  }
  return `Etapa ${indice + 1} de ${etapas.length}: ${etapas[indice].rotulo}`;
};

/* ------------------------------------------------------------------------
 * Painel do sino (redesign): textos curtos e derivados puros
 * --------------------------------------------------------------------- */

/**
 * "há 5 min", "há 9 h", "há 2 d". Recebe `agora` explícito (nunca chame
 * `Date.now()` no render: só o cliente calcula, por causa da hidratação).
 */
export const tempoRelativoCurto = (iso: string, agora: number): string => {
  const momento = new Date(iso).getTime();
  if (Number.isNaN(momento)) return '';
  const segundos = Math.floor((agora - momento) / 1000);
  if (segundos < 60) return 'agora';
  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  return `há ${Math.floor(horas / 24)} d`;
};

/**
 * Texto da pílula de status: "Requer sua ação" só se o item ainda está na
 * Minha fila; senão o estado de destino em caixa de frase, sem o sufixo entre
 * parênteses ("Publicado Nacional" -> "Publicado nacional").
 */
export const rotuloStatus = (n: Notificacao, naFila: boolean): string => {
  if (naFila) return 'Requer sua ação';
  const base = (n.para_estado_titulo || n.transicao_titulo || '')
    .replace(/\s*\(.*?\)\s*/g, ' ')
    .trim();
  if (!base) return '';
  return (
    base.charAt(0).toLocaleUpperCase('pt-BR') +
    base.slice(1).toLocaleLowerCase('pt-BR')
  );
};
